import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { getDefaultUserImageUrl } from './functions';
import FileUploadApi from '../api/fileUploadApi';
import UserProfileApi from '../api/userProfileApi';
import UserProfileData from '../types/userProfileData';
import { User } from 'firebase/auth';
import { UserProfileService } from '../services/userProfileService';
import { AuthContext } from './AuthProvider';

export function usePrevious<T>(value: T): T | undefined {
  const c = useRef<T>(value);
  const p = useRef<T>();
  useEffect(() => {
    p.current = c.current;
    c.current = value;
  }, [value]);
  return p.current;
}

/**
 * Returns a URL pointing to the file passed as argument. Handles allocating and releasing the underlying object URL.
 * @param file
 */
export function useFileTemporaryUrl(file: File | undefined): string | undefined {
  const [fileUrl, setFileUrl] = useState<string>();
  // Used to release memory of created object for previous file when a new file is selected. We can't use useEffect
  // on fileUrl because function fires after value changes, so we lose the url to the object we want to release.
  const previousFileUrl = usePrevious<string | undefined>(fileUrl);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setFileUrl(url);
    } else {
      setFileUrl(undefined);
    }
  }, [file]);

  // Clean up the current object when component is unmounted.
  useEffect(() => {
    return () => (fileUrl ? URL.revokeObjectURL(fileUrl) : undefined);
  }, [fileUrl]);

  // Clean up the previous object when file changes.
  useEffect(() => {
    if (previousFileUrl) {
      URL.revokeObjectURL(previousFileUrl);
    }
  }, [previousFileUrl]);

  return fileUrl;
}

export function useStorageFileUrl(path: string | undefined): string | null | undefined {
  const [url, setUrl] = useState<string | null>();

  useEffect(() => {
    async function fetchData() {
      if (path) {
        const fetchedUrl = await FileUploadApi.getUrlFromPath(path);
        setUrl(fetchedUrl);
      } else if (path === '') {
        setUrl(null);
      }
    }

    fetchData();
  }, [path]);

  return url;
}

export function useProfileImageUrl(path: string | undefined): string | undefined {
  const url = useStorageFileUrl(path);
  if (url !== null) {
    return url;
  } else {
    return getDefaultUserImageUrl();
  }
}

/**
 *
 * Returns a URL to the file that will be saved, filename of the file that will be saved and a function to perform the
 * storage update.
 *
 * @param fileOrDelete file to upload or the string 'DELETE_ONLY'. If the value is a file, the updateStorage function
 * will upload the file to newStoragePath and delete object at storagePathToDelete. If the value is 'DELETE_ONLY', the
 * updateStorage function will only delete object at storagePathToDelete. If the value is undefined, the updateStorage
 * function will do nothing.
 * @param storagePathToDelete path in storage to object that should be deleted when updating storage. Will not be
 * deleted if fileOrDelete is undefined.
 * @param newStoragePathState state of storage path that is to be updated.
 * @param folderPath path to folder in storage where file should be uploaded without trailing or leading slashes ('/').
 * No need to include file name in this path, it will be automatically generated.
 */
export function useFileUpload(
  fileOrDelete: File | 'DELETE_ONLY' | undefined,
  storagePathToDelete: string | undefined,
  newStoragePathState: [string, Dispatch<SetStateAction<string>>],
  folderPath: string
) {
  const [newStoragePath, setNewStoragePath] = newStoragePathState;
  const [initialStorageUrl, setInitialStorageUrl] = useState<string | null>();
  const file = fileOrDelete !== 'DELETE_ONLY' ? fileOrDelete : undefined;
  const tempUrl = useFileTemporaryUrl(file);

  useEffect(() => {
    if (storagePathToDelete === undefined) return;
    // If empty string we know that there is no storage path.
    if (storagePathToDelete === '') {
      setInitialStorageUrl(null);
      return;
    }

    async function getUrl(path: string) {
      const fetchedUrl = await FileUploadApi.getUrlFromPath(path);
      setInitialStorageUrl(fetchedUrl);
    }

    getUrl(storagePathToDelete);
  }, [storagePathToDelete]);

  useEffect(() => {
    if (fileOrDelete && fileOrDelete !== 'DELETE_ONLY') {
      const newPath = `${folderPath}/${uuidv4().trim().substring(0, 6)}-${fileOrDelete.name}`;
      setNewStoragePath(newPath);
    } else {
      setNewStoragePath('');
    }
  }, [fileOrDelete, folderPath, setNewStoragePath]);

  async function updateStorage() {
    if (fileOrDelete === 'DELETE_ONLY' && storagePathToDelete) {
      return await FileUploadApi.deleteFile(storagePathToDelete);
    } else if (file && newStoragePath) {
      storagePathToDelete && (await FileUploadApi.deleteFile(storagePathToDelete));
      return await FileUploadApi.uploadFile(file, newStoragePath);
    }
  }

  const previewUrl = fileOrDelete === 'DELETE_ONLY' ? null : tempUrl || initialStorageUrl;

  let previewFilename: string | undefined;
  if (fileOrDelete && fileOrDelete !== 'DELETE_ONLY') {
    previewFilename = fileOrDelete.name;
  } else if (fileOrDelete === undefined && storagePathToDelete) {
    previewFilename = /[^/]*$/.exec(storagePathToDelete)?.[0];
  }

  return { previewUrl, previewFilename, updateStorage };
}

/**
 * Returns the user profile with given uuid.
 * @param uuid
 */
export function useUserProfile(uuid: string | undefined | null) {
  const [userProfile, setUserProfile] = useState<UserProfileData | null>();

  useEffect(() => {
    async function fetchData() {
      if (!uuid) return;
      const fetchedUserProfile = await UserProfileApi.getUserProfileByUuid(uuid);
      setUserProfile(fetchedUserProfile || null);
    }
    fetchData();
  }, [uuid]);

  return uuid !== null ? userProfile : null;
}

/**
 * This hook is used to keep track whether an Div element is within the viewport of the browser window.
 *
 * @returns void
 */
export function useInViewport() {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isInViewPort, setIsInViewPort] = useState<boolean>(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const isInViewPort = !!entries[0]?.isIntersecting;

        setIsInViewPort(isInViewPort);
      },
      {
        threshold: 0.5, // Threshold for when isIntersecting is set to true. 50% of element is shown then it swaps in sidebar
      }
    );
    if (!elementRef.current) return; // Check that it is not undefined
    observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, []);

  return { elementRef, isInViewPort };
}

export function useIsModerator(user: User | null) {
  const [isModerator, setIsModerator] = useState<boolean>();

  useEffect(() => {
    async function fetchData() {
      const fetchedIsModerator = await UserProfileService.isModerator(user);
      setIsModerator(fetchedIsModerator);
    }
    fetchData();
  }, [user]);

  return isModerator;
}

export function useIsCurrentUserModerator() {
  const { user } = useContext(AuthContext)!;
  return useIsModerator(user);
}
